import { HStack, VStack, Text } from '@chakra-ui/react';
import {
  File,
  FileDoc,
  FileJpg,
  FilePdf,
  FilePng,
  FileXls,
  FileZip,
} from '@phosphor-icons/react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { Maybe } from '@/generated/graphql';
import formatBytes from '@/lib/formatBytes';
import { attachmentUrl } from '@/lib/urls';
import Link from '../frontend/Link';

export type AttachmentItemType =
  | Maybe<{
      container: string;
      file_path: string;
      original_filename: string;
      mime_type: string;
      file_size_bytes: string;
    }>
  | {
      url: string;
      original_filename: string;
      mime_type: string;
    }
  | undefined;

export const getFileIcon = (original_filename: string) => {
  const extension = original_filename.split('.').at(-1) || '';

  if (/^docx?$/i.test(extension)) {
    return FileDoc;
  }
  if (/^pdf$/i.test(extension)) {
    return FilePdf;
  }
  if (/^zip$/i.test(extension)) {
    return FileZip;
  }
  if (/^jpe?g$/i.test(extension)) {
    return FileJpg;
  }
  if (/^png$/i.test(extension)) {
    return FilePng;
  }
  if (/^xls.?$/i.test(extension)) {
    return FileXls;
  }

  return File;
};

const AttachmentItem = ({
  attachment,
  type,
}: {
  attachment: AttachmentItemType;
  type?: string;
}) => {
  const { t } = useTranslation(['frontoffice', 'backoffice']);
  const { locale } = useRouter();

  if (!attachment) {
    return null;
  }

  const FileIcon = getFileIcon(attachment.original_filename);

  return (
    <Link
      href={
        'url' in attachment
          ? attachment.url
          : attachmentUrl(attachment, { withHostname: true, locale })
      }
      target="_blank"
    >
      <HStack>
        <FileIcon size={36} />
        <VStack alignItems="flex-start" spacing={0}>
          {type ? (
            <Text color="primary.500">{t(`backoffice:${type}`)}</Text>
          ) : null}
          <Text color="gray.500" fontSize="xs">
            {`${attachment.original_filename}`}{' '}
            {'file_size_bytes' in attachment
              ? `(${formatBytes(Number(attachment.file_size_bytes))})`
              : null}
          </Text>
        </VStack>
      </HStack>
    </Link>
  );
};

export default AttachmentItem;
